/**
 * GenerateFlowsButton — issue #863, epic #831.
 *
 * Triggers POST /:id/e2e-flows/generate. Used in two places:
 *   - StitchReviewPage: inside the "stitches changed — regenerate"
 *     banner (shown when useStitchDirty().isDirty).
 *   - ProjectPage: as a CTA card after analysis completes when no
 *     flows have been generated yet.
 *
 * Surfaces orphaned-stitch warnings (when the analysis has moved
 * since the user authored a stitch) so the user knows to reattach.
 */

import { useState } from 'react';
import { api } from '../../services/api';
import { useStitchDirty } from '../../hooks/useStitchDirty';

interface GenerateFlowsButtonProps {
  projectId: string;
  /** Called on success with the generation result so the parent page can refresh. */
  onSuccess?: (result: GenerateResult) => void;
  /** Visual variant. Banner = inline pill. Cta = larger primary button. */
  variant?: 'banner' | 'cta';
  /** Label override; defaults match the variant. */
  label?: string;
}

interface GenerateResult {
  flowCount: number;
  diagramId: string;
  generatedAt: string;
  durationMs: number;
  stitchSnapshot: {
    auto: number;
    user: number;
    ai: number;
    unresolved: number;
    ambiguous: number;
  };
  orphanedStitches: { user: number; ai: number; auto: number };
}

export function GenerateFlowsButton({
  projectId,
  onSuccess,
  variant = 'banner',
  label,
}: GenerateFlowsButtonProps) {
  const { clearDirty } = useStitchDirty(projectId);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<GenerateResult | null>(null);

  const buttonLabel =
    label ?? (variant === 'cta' ? 'Generate E2E Flows' : 'Regenerate flows');

  const handleClick = async () => {
    setError(null);
    setIsGenerating(true);
    try {
      const result = await api.generateE2EFlows(projectId);
      setLastResult(result);
      clearDirty();
      onSuccess?.(result);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Generation failed';
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const baseClasses = variant === 'cta'
    ? 'px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
    : 'px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50';

  return (
    <div className="inline-flex flex-col gap-1">
      <button
        type="button"
        onClick={handleClick}
        disabled={isGenerating}
        className={baseClasses}
      >
        {isGenerating ? 'Generating…' : buttonLabel}
      </button>

      {error && (
        <span className="text-xs text-red-600">{error}</span>
      )}

      {lastResult && !error && (
        <span className="text-xs text-gray-600">
          {lastResult.flowCount} flow{lastResult.flowCount === 1 ? '' : 's'} in{' '}
          {Math.round(lastResult.durationMs / 100) / 10}s
          {lastResult.orphanedStitches.user > 0 && (
            <span className="ml-2 text-amber-600">
              · {lastResult.orphanedStitches.user} stitch
              {lastResult.orphanedStitches.user === 1 ? '' : 'es'} orphaned —
              reattach
            </span>
          )}
        </span>
      )}
    </div>
  );
}
